<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { ArrowLeftIcon, CheckIcon } from '@heroicons/vue/24/outline'
import { printCurrency } from '@/utils/numbers'
import { formatDate } from '@/utils/dateUtils'

const router = useRouter()
const suiteStore = useSuiteStore()
const boostCoreStore = useBoostCorePermissionStore()
const usersStore = useBoostCoreUsersStore()

const selectedCommercialUuid = ref<string | null>(null)
const periodFrom = ref<string | null>(null)
const periodTo = ref<string | null>(null)
const isLoading = ref(false)
const dashboard = ref<{
  rows: any[];
  contractTypes: { code: string; label: string }[];
  paymentModes: { code: string; label: string; type: string }[];
  availableVariables: { uuid: string; period: string }[];
} | null>(null)
const savingUuid = ref<string | null>(null)
const errorMsg = ref<string | null>(null)
const showValidated = ref(false)

const filteredRows = computed(() => {
  if (!dashboard.value) return []
  if (showValidated.value) return dashboard.value.rows
  return dashboard.value.rows.filter((r: any) => r.variableStatus === 'INIT')
})


const userOptions = computed(() => {
  const active = usersStore.users.filter((u) => u.status === 'ACTIVE')
  return active.map((u) => ({
    id: u.uuid,
    value: u.uuid,
    label: usersStore.getUserLabel(u),
  }))
})

const periodOptions = computed(() => {
  const opts: { id: string; value: string | null; label: string }[] = [{ id: 'all', value: null, label: 'Toutes' }]
  const now = new Date()
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    opts.push({ id: val, value: val, label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) })
  }
  return opts
})

const editedRows = ref<Record<string, { durationCoef?: number; iavCoef?: number; typeContratCode?: string; paymentModeCode?: string; variableUuid?: string }>>({})

function getEdited(uuid: string) {
  return editedRows.value[uuid] || {}
}

function setEdited(uuid: string, field: string, value: number | string | undefined) {
  if (!editedRows.value[uuid]) editedRows.value[uuid] = {}
  if (value === undefined || value === '') {
    delete (editedRows.value[uuid] as any)[field]
  } else {
    (editedRows.value[uuid] as any)[field] = value
  }
  editedRows.value = { ...editedRows.value }
}

function getDisplayValue(row: any, field: string) {
  const e = (getEdited(row.uuid) as Record<string, number | string | undefined>)[field]
  if (e !== undefined) return e
  return row[field]
}

function hasChanges(uuid: string) {
  return Object.keys(getEdited(uuid)).length > 0
}

function clampCoef(v: number) {
  return Math.max(0.5, Math.min(1.5, v))
}

async function fetchDashboard() {
  if (!selectedCommercialUuid.value) return
  isLoading.value = true
  errorMsg.value = null
  try {
    const params = new URLSearchParams({ salesEngineerUuid: selectedCommercialUuid.value })
    if (periodFrom.value) params.set('periodFrom', periodFrom.value)
    if (periodTo.value) params.set('periodTo', periodTo.value)
    const res = await suiteStore.suiteFetch(`orders/commission-dashboard?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!res || typeof res === 'boolean' || !res.ok) {
      const txt = res && typeof res !== 'boolean' ? await res.text() : ''
      throw new Error(txt || 'Erreur chargement')
    }
    dashboard.value = await res.json()
    editedRows.value = {}
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e)
    dashboard.value = null
  } finally {
    isLoading.value = false
  }
}

async function saveRow(row: any) {
  const edits = getEdited(row.uuid)
  if (Object.keys(edits).length === 0) return
  savingUuid.value = row.uuid
  errorMsg.value = null
  try {
    const body: any = {}
    if (edits.typeContratCode != null) body.typeContratCode = edits.typeContratCode
    if (edits.durationCoef != null) body.durationCoef = clampCoef(edits.durationCoef)
    if (edits.paymentModeCode != null) body.paymentModeCode = edits.paymentModeCode
    if (edits.iavCoef != null) body.iavCoef = clampCoef(edits.iavCoef)
    if (edits.variableUuid != null) body.variableUuid = edits.variableUuid
    const res = await suiteStore.suiteFetch(`orders/commission/${row.uuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
    if (!res || typeof res === 'boolean' || !res.ok) {
      const txt = res && typeof res !== 'boolean' ? await res.text() : ''
      throw new Error(txt || 'Erreur sauvegarde')
    }
    setEdited(row.uuid, 'typeContratCode', undefined)
    setEdited(row.uuid, 'durationCoef', undefined)
    setEdited(row.uuid, 'paymentModeCode', undefined)
    setEdited(row.uuid, 'iavCoef', undefined)
    setEdited(row.uuid, 'variableUuid', undefined)
    await fetchDashboard()
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e)
  } finally {
    savingUuid.value = null
  }
}

function variableOptions(row: any) {
  const list = dashboard.value?.availableVariables || []
  const opts = list.map((v) => ({ id: v.uuid, value: v.uuid, label: v.period }))
  if (row.variableUuid && !opts.some((o) => o.value === row.variableUuid)) {
    opts.unshift({ id: row.variableUuid, value: row.variableUuid, label: row.variablePeriod || row.variableUuid })
  }
  return opts
}

watch([selectedCommercialUuid, periodFrom, periodTo], () => {
  if (selectedCommercialUuid.value) fetchDashboard()
})

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  if (usersStore.users.length === 0) await usersStore.fetchUsers()
  if (selectedCommercialUuid.value) fetchDashboard()
})

function goBack() {
  router.push('/dashboard/orders')
}
</script>

<template>
  <div class="p-4">
    <div class="flex items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-2">
        <button class="btn btn-ghost btn-sm" @click="goBack">
          <ArrowLeftIcon class="w-5 h-5" />
        </button>
        <Bs2Title title="Dashboard commissions" subtitle="SUPER_ADMIN – édition des coefficients et variables" />
      </div>
    </div>

    <div class="card bg-base-100 shadow mb-6">
      <div class="card-body">
        <h3 class="font-semibold">STEP 1 – Choix du commercial et de la période</h3>
        <div class="flex flex-wrap gap-4 items-end">
          <SelectInput
            v-model="selectedCommercialUuid"
            label="Commercial"
            :options="userOptions"
            placeholder="Sélectionner un commercial"
            required
            class="min-w-[220px]"
          />
          <SelectInput
            v-model="periodFrom"
            label="Période (début)"
            :options="periodOptions"
            placeholder="Optionnel"
            class="min-w-[180px]"
          />
          <SelectInput
            v-model="periodTo"
            label="Période (fin)"
            :options="periodOptions"
            placeholder="Optionnel"
            class="min-w-[180px]"
          />
        </div>
      </div>
    </div>

    <div v-if="errorMsg" class="alert alert-error mb-4">
      {{ errorMsg }}
    </div>

    <div v-if="isLoading" class="flex justify-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else-if="dashboard && selectedCommercialUuid" class="card bg-base-100 shadow">
      <div class="card-body overflow-x-auto">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold">STEP 2, 3 & 4 – Tableau des commissions (sans pagination)</h3>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" class="checkbox checkbox-sm" v-model="showValidated" />
            <span class="text-sm">Afficher les commissions validées</span>
          </label>
        </div>
        <p class="text-sm text-base-content/70 mb-4">
          Coefficients éditables entre 0.5 et 1.5. Variable modifiable uniquement si statut INIT.
        </p>
        <table class="table table-xs table-pin-rows">
          <thead>
            <tr>
              <th>N° Devis</th>
              <th>Création</th>
              <th>Envoi</th>
              <th>Validation</th>
              <th>Type contrat</th>
              <th>Coef contrat</th>
              <th>Coef durée</th>
              <th>Mode paiement</th>
              <th>Coef paiement</th>
              <th>Coef IAV</th>
              <th>Statut</th>
              <th>Variable (mois)</th>
              <th>Marge compteur</th>
              <th>Commission</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredRows" :key="row.uuid">
              <td class="font-mono">{{ row.quoteReference }}</td>
              <td>{{ row.orderCreatedAt ? formatDate(row.orderCreatedAt.toString()) : '–' }}</td>
              <td>{{ row.orderSentAt ? formatDate(row.orderSentAt.toString()) : '–' }}</td>
              <td>{{ row.orderValidatedAt ? formatDate(row.orderValidatedAt.toString()) : '–' }}</td>
              <!-- Type contrat (select) -->
              <td>
                <select
                  class="select select-bordered select-xs min-w-[120px]"
                  :value="getDisplayValue(row, 'typeContratCode') ?? row.typeContratCode ?? ''"
                  @change="(e: any) => setEdited(row.uuid, 'typeContratCode', e.target.value || undefined)"
                >
                  <option value="">–</option>
                  <option
                    v-for="ct in (dashboard?.contractTypes || [])"
                    :key="ct.code"
                    :value="ct.code"
                  >{{ ct.label }}</option>
                </select>
              </td>
              <!-- Coef contrat (lecture seule) -->
              <td class="text-center">{{ row.contractCoef }}</td>
              <!-- Coef durée (input) -->
              <td>
                <input
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="1.5"
                  class="input input-bordered input-xs w-16"
                  :value="getDisplayValue(row, 'durationCoef')"
                  @input="(e: any) => setEdited(row.uuid, 'durationCoef', e.target.value ? Number(e.target.value) : undefined)"
                />
              </td>
              <!-- Mode paiement (select) -->
              <td>
                <select
                  class="select select-bordered select-xs min-w-[140px]"
                  :value="getDisplayValue(row, 'paymentModeCode') ?? row.paymentModeCode ?? ''"
                  @change="(e: any) => setEdited(row.uuid, 'paymentModeCode', e.target.value || undefined)"
                >
                  <option value="">–</option>
                  <option
                    v-for="pm in (dashboard?.paymentModes || [])"
                    :key="pm.code"
                    :value="pm.code"
                  >{{ pm.label }}</option>
                </select>
              </td>
              <!-- Coef paiement (lecture seule) -->
              <td class="text-center">{{ row.paymentCoef }}</td>
              <!-- Coef IAV (input) -->
              <td>
                <input
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="1.5"
                  class="input input-bordered input-xs w-16"
                  :value="getDisplayValue(row, 'iavCoef')"
                  @input="(e: any) => setEdited(row.uuid, 'iavCoef', e.target.value ? Number(e.target.value) : undefined)"
                />
              </td>
              <td>
                <span
                  class="badge badge-sm"
                  :class="row.canChangeVariable ? 'badge-info' : 'badge-success'"
                >{{ row.variableStatus || '–' }}</span>
              </td>
              <td>
                <select
                  v-if="row.canChangeVariable"
                  class="select select-bordered select-xs min-w-[100px]"
                  :value="getDisplayValue(row, 'variableUuid') ?? row.variableUuid ?? ''"
                  @change="(e: any) => setEdited(row.uuid, 'variableUuid', e.target.value || undefined)"
                >
                  <option value="">–</option>
                  <option v-for="opt in variableOptions(row)" :key="opt.id" :value="opt.value">{{ opt.label }}</option>
                </select>
                <span v-else>{{ row.variablePeriod || '–' }}</span>
              </td>
              <td>{{ printCurrency(row.margeCompteur) }}</td>
              <td>{{ printCurrency(row.commission) }}</td>
              <td>
                <ButtonDaisy
                  v-if="hasChanges(row.uuid)"
                  text="Valider"
                  variant="primary"
                  size="sm"
                  :icon="CheckIcon"
                  :disabled="savingUuid === row.uuid"
                  @click="saveRow(row)"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="filteredRows.length === 0" class="text-base-content/60 py-4">Aucune commission.</p>
      </div>
    </div>
  </div>
</template>
